/**
 * New Invoice Page - T098
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { InvoiceForm } from '../components/invoices/InvoiceForm';
import { useCreateInvoice, useMarkInvoiceSent } from '../hooks/useInvoices';
import { useToastHelpers } from '../components/ui/Toast';
import { api } from '../services/api';
import type { CreateInvoiceInput, CreateInvoiceItemInput } from '@armard/shared';
import { useState } from 'react';

export const Route = createFileRoute('/invoices_/new')({
  component: NewInvoicePage,
});

function NewInvoicePage() {
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const createInvoice = useCreateInvoice();
  const markSent = useMarkInvoiceSent();
  const [isSending, setIsSending] = useState(false);

  const createInvoiceWithItems = async (invoiceData: CreateInvoiceInput, items: CreateInvoiceItemInput[]) => {
    const invoice = await createInvoice.mutateAsync(invoiceData);

    for (const itemData of items) {
      await api.post(`/invoices/${invoice.id}/items`, itemData);
    }

    return invoice;
  };

  const handleSubmit = async (invoiceData: CreateInvoiceInput, items: CreateInvoiceItemInput[]) => {
    try {
      const invoice = await createInvoiceWithItems(invoiceData, items);
      toast.success('Rechnung erstellt');
      navigate({ to: '/invoices/$id', params: { id: invoice.id } });
    } catch {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleSubmitAndSend = async (invoiceData: CreateInvoiceInput, items: CreateInvoiceItemInput[]) => {
    try {
      setIsSending(true);
      const invoice = await createInvoiceWithItems(invoiceData, items);
      await markSent.mutateAsync({ id: invoice.id, version: invoice.version });
      toast.success('Rechnung erstellt und als gesendet markiert');
      navigate({ to: '/invoices/$id', params: { id: invoice.id } });
    } catch {
      toast.error('Fehler beim Erstellen');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Link to="/invoices" className="text-white/40 hover:text-white/60">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <span className="text-white/40">Rechnungen</span>
          </div>
          <h1 className="text-2xl font-semibold text-white">Neue Rechnung</h1>
        </div>

        <InvoiceForm
          onSubmit={handleSubmit}
          onSubmitAndSend={handleSubmitAndSend}
          isLoading={createInvoice.isPending}
          isSending={isSending}
          onCancel={() => navigate({ to: '/invoices' })}
        />
      </AppShell>
    </ProtectedRoute>
  );
}
